import { Redirect, router, useLocalSearchParams } from 'expo-router';
import React, { useCallback } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import {
  CloseConfirmationModal,
  GiftCardActionSheet,
  GiftCardProvider,
  useGiftCardsActionSheet,
} from '@fhs/payments';
import { ActionSheet, tokens } from '@fhs/ui';
import { CART_PAYMENT_PAGE } from '@fhs-legacy/frontend/src/utils/test-ids';

import { PaymentMethodSelection } from '../components/checkout-components/payment-details/action-payment-method-selection';
import { CartActionPickupTimeSelector } from '../components/checkout-components/service-mode-details/pickup/action-pickup-time-selector';
import { usePaymentMethodStore } from '../state/payment-stores';

import { CheckoutScreen as CheckoutScreen_ } from './_checkout';

const onClose = () => router.setParams({ action: undefined });

export function CheckoutScreen() {
  const cart = { state: undefined, rbiOrderId: undefined };
  const isPending = false;

  const { action } = useLocalSearchParams<{
    action: 'time' | 'service-mode' | 'payment';
  }>();
  const showTimeSelector = action === 'time';
  const showPaymentSelector = action === 'payment';
  const showServiceModeSelector = action === 'service-mode';

  // Gift card action sheet state management
  const {
    actionSheetOpen: giftCardActionSheetOpen,
    handleActionSheetOpen: handleGiftCardActionSheetOpen,
    handleClose: handleGiftCardActionSheetClose,
    handleRequestClose: handleGiftCardRequestClose,
    currentStep: giftCardCurrentStep,
    setCurrentStep: setGiftCardCurrentStep,
    headerTitle: giftCardHeaderTitle,
    closeConfirmationOpen: giftCardCloseConfirmationOpen,
    handleConfirmStay: handleGiftCardConfirmStay,
    handleConfirmLeave: handleGiftCardConfirmLeave,
    cardId: giftCardCardId,
    setCardId: setGiftCardCardId,
    pin: giftCardPin,
    setPin: setGiftCardPin,
  } = useGiftCardsActionSheet();

  const setSelectedPaymentMethod = usePaymentMethodStore(state => state.setSelectedPaymentMethod);
  const setShowAddPaymentMethod = usePaymentMethodStore(state => state.setShowAddPaymentMethod);
  const setPreservePaymentMethodSelection = usePaymentMethodStore(
    state => state.setPreservePaymentMethodSelection
  );

  const handleGiftCardSaved = useCallback(() => {
    // Prevent auto-selection from overriding the gift card selection when userAccounts refetches
    setPreservePaymentMethodSelection(true);
    setShowAddPaymentMethod(false);
    setSelectedPaymentMethod('giftCard');
    handleGiftCardActionSheetClose();
  }, [
    setSelectedPaymentMethod,
    setShowAddPaymentMethod,
    setPreservePaymentMethodSelection,
    handleGiftCardActionSheetClose,
  ]);

  if (isPending) {
    return (
      <View style={styles.page}>
        <ActivityIndicator />
      </View>
    );
  }

  if (!cart) {
    throw new Error('UndetectableCartError');
  }

  if (cart.state === 'INSERT_REQUESTED') {
    return null;
  }
  if (cart.state === 'INSERT_SUCCESSFUL' && cart.rbiOrderId) {
    return <Redirect href={`/order-confirmation/${cart.rbiOrderId}`} />;
  }

  return (
    <GiftCardProvider>
      <View testID={CART_PAYMENT_PAGE} style={styles.page}>
        <View style={styles.inner}>
          <CheckoutScreen_ />
        </View>
      </View>
      {showTimeSelector && (
        <ActionSheet isVisible onClose={onClose}>
          <CartActionPickupTimeSelector onClose={onClose} />
        </ActionSheet>
      )}
      {showServiceModeSelector && (
        <ActionSheet isVisible onClose={onClose}>
          service mode
        </ActionSheet>
      )}
      {showPaymentSelector && (
        <ActionSheet isVisible onClose={onClose} isScrollableDrawer>
          <PaymentMethodSelection
            onClose={onClose}
            onAddNewGiftCardPress={handleGiftCardActionSheetOpen}
          />
        </ActionSheet>
      )}

      <GiftCardActionSheet
        headerTitle={giftCardHeaderTitle}
        isVisible={giftCardActionSheetOpen}
        currentStep={giftCardCurrentStep}
        setCurrentStep={setGiftCardCurrentStep}
        onClose={handleGiftCardRequestClose}
        onGiftCardSaved={handleGiftCardSaved}
        cardId={giftCardCardId}
        onCardIdChange={setGiftCardCardId}
        pin={giftCardPin}
        onPinChange={setGiftCardPin}
      />
      {/* Confirmation modal rendered at parent level to avoid nested Modal issue on iOS */}
      <CloseConfirmationModal
        isOpen={giftCardCloseConfirmationOpen}
        onConfirm={handleGiftCardConfirmStay}
        onDismiss={handleGiftCardConfirmLeave}
      />
    </GiftCardProvider>
  );
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: 'white',
    borderTopColor: tokens.colors.$black10,
    borderTopWidth: 1,
    flex: 1,
  },
  inner: {
    flex: 1,
    width: '100%',
    maxWidth: 908,
    marginHorizontal: 'auto',
  },
});
